import React, { useState, useRef, useEffect } from "react";
import { db, storage } from "./firebase"; 
import { ref, getDownloadURL, uploadBytesResumable, deleteObject } from "firebase/storage";
import { doc, collection, addDoc, updateDoc, getDocs, arrayUnion, arrayRemove, deleteDoc, increment } from "firebase/firestore";
import VinylPlayer from "./VinylPlayer";
import { calculateTrackScore } from './services/AnalyticsEngine';
import DJMixSection from "./DJMixSection"; 

const formatTime = (time) => {
  if (isNaN(time)) return "00:00";
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

export default function RadioPage({ 
  user, isPlaying, togglePlay, playTrack, tracks, audioRef,
  currentTrackIndex, currentTrack, progress, duration, setAudioTime,
  isRandom, setIsRandom, allUsers, isUserAdmin, isUserArtist,
  activeFilter, setActiveFilter, filteredTracks, handleShare,
  sortOrder, setSortOrder, playPreviousTrack, showToast,
  isStationPath, searchTerm, setSearchTerm, isShuffle, 
  setIsShuffle, playNextTrack, onLoginClick, onJoinClick,
  djMode, audioContextRef, limiterNodeRef, sourceNodeRef,
  showAllTracks, setShowAllTracks, mixcloudQueue, setMixcloudQueue, 
  activeMixcloudItem, setActiveMixcloudItem
}) {
  

  // NEU: UPLOAD GAIN ANALYSER

  const analyzeFileGain = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
  
      const float32Data = audioBuffer.getChannelData(0);
      let sumSquares = 0;
      for (let i = 0; i < float32Data.length; i++) {
        sumSquares += float32Data[i] * float32Data[i];
      }
      const rms = Math.sqrt(sumSquares / float32Data.length);
      const targetRms = 0.15;
      let adjustment = targetRms / rms;
      return Number(Math.max(0.5, Math.min(2.5, adjustment)).toFixed(2));
    } catch (err) {
      console.error("Gain-Analyse fehlgeschlagen:", err);
      return 1.0; // Sicherer Fallback
    }
  };



  const [isArchiveOpen, setIsArchiveOpen] = useState(isStationPath);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [meta, setMeta] = useState({ title: "", artist: "", style: "Dubcircus", year: 2026 });
  const [editingTrack, setEditingTrack] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [easterEggClicks, setEasterEggClicks] = useState(0);
  const [showPlaylist, setShowPlaylist] = useState(false);
  const activeTrackRef = useRef(null);

  // ─── MIXCLOUD HELPERS ─────────────────────────────
  const [mixcloudInput, setMixcloudInput] = useState('');

  const extractMixcloudKey = (url) => {
    try {
      const parts = new URL(url).pathname.split('/').filter(Boolean);
      if (parts.length >= 2) return `/${parts[0]}/${parts[1]}/`;
    } catch(e) {}
    return null;
  };

  const addMixcloudToQueue = () => {
    const url = mixcloudInput.trim();
    const key = extractMixcloudKey(url);
    if (!key) { showToast('Ungültige Mixcloud URL', 'error'); return; }
    const titleParts = key.split('/').filter(Boolean);
    const title = titleParts[1]?.replace(/-/g, ' ').toUpperCase() || 'MIXCLOUD MIX';
    const artist = titleParts[0] || 'Mixcloud';
    setMixcloudQueue(q => [...q, {
      id: Date.now() + '', title, artist, mixcloudKey: key, type: 'mixcloud'
    }]);
    setMixcloudInput('');
    showToast('Mixcloud Mix zur Queue hinzugefügt ☁️');
  };

  const safeTracks = Array.isArray(tracks) ? tracks : [];
  const safeFilteredTracks = Array.isArray(filteredTracks) ? filteredTracks : [];

  // NEU: Zusätzlicher Filter für den Button
  const finalDisplayedTracks = showAllTracks 
  ? safeFilteredTracks 
  : safeFilteredTracks.filter(t => t.onAir === true);

  const publicCount = safeFilteredTracks.filter(t => t.onAir === true).length;

  const currentUserData = allUsers?.find(u => u.id === user?.uid || u.uid === user?.uid);
  const canUpload = isStationPath && !!user && (isUserAdmin || isUserArtist || currentUserData?.role === "artist");

  const styles = ["All", "TOP ♡", "Dubcircus", "Reggae", "Electronic", "Ambient", "Experimental", "streetwise Lab", "SoundCheck"];
  const adminStyles = [...styles, "🆕 NEW", "🗑 DISLIKED", "🔒 HIDDEN"];

  const startUpload = async () => {
    if (!selectedFile || !meta.title) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      // ✅ NEU: Analysiere die Datei lokal beim User, bevor der Upload startet
      const autoGain = await analyzeFileGain(selectedFile);
      console.log("Berechneter Gain für neuen Upload:", autoGain);
      const fileName = `${Date.now()}_${selectedFile.name}`;
      const storageRef = ref(storage, `music/${user.uid}/${fileName}`);
      const uploadTask = uploadBytesResumable(storageRef, selectedFile);
      uploadTask.on('state_changed', 
        (snap) => setUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)), 
        (err) => { throw err; }, 
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          const fileSizeMB = parseFloat((selectedFile.size / (1024 * 1024)).toFixed(2));
          const artistName = meta.artist || (currentUserData?.name || user.displayName || user.email.split('@')[0].toUpperCase());
          const newTrackData = {
            ...meta, artist: artistName, url: downloadURL, userId: user.uid, volume_adjustment: autoGain,
            likes: [], dislikes: [], comments: [], year: meta.year || 2026,
            createdAt: new Date(), fileSizeMB, plays: 0, totalPlaytimeSec: 0,
            skipCount: 0
          };
          const initialScore = calculateTrackScore(newTrackData);
          await addDoc(collection(db, "tracks"), {
            ...newTrackData,
            score: 14,        // ✅ direkt 14 statt berechnen
            onAir: true       // ✅ neuer Track ist automatisch onAir
          });
          setSelectedFile(null);
          setUploading(false);
          showToast("Track erfolgreich hochgeladen! ✅");
        }
      );
    } catch (err) { 
      setUploading(false); 
      showToast("Upload failed: " + err.message, "error");
    }
  };

  const getAnonymousId = () => {
    let id = localStorage.getItem("anon_id");
    if (!id) {
      id = "anon_" + Math.random().toString(36).slice(2);
      localStorage.setItem("anon_id", id);
    }
    return id;
  };

  const toggleLike = async (trackId, likesArray = []) => {
    const voterId = user ? user.uid : getAnonymousId();
    const trackRef = doc(db, "tracks", trackId);
    const safeLikes = Array.isArray(likesArray) ? likesArray : [];
    const isCurrentlyLiked = safeLikes.includes(voterId);
    const currentTrackData = filteredTracks.find(t => t.id === trackId) || {};
    try {
      const updatedLikes = isCurrentlyLiked
        ? safeLikes.filter(id => id !== voterId)
        : [...safeLikes, voterId];
      const newScore = calculateTrackScore({ ...currentTrackData, likes: updatedLikes });
      const today = new Date().toISOString().split('T')[0];

      await updateDoc(trackRef, { 
        likes: isCurrentlyLiked ? arrayRemove(voterId) : arrayUnion(voterId),
        dislikes: arrayRemove(voterId),
        onAir: newScore > 0,
        score: newScore,
        // ✅ NEU: dailyStats für Likes
        [`dailyStats.${today}.likes`]: isCurrentlyLiked ? increment(-1) : increment(1),
    });
    } catch (err) { 
      console.error("Like Error:", err);
    }
  };

  const toggleDislike = async (trackId, dislikesArray = []) => {
    const voterId = user ? user.uid : getAnonymousId();
    const trackRef = doc(db, "tracks", trackId);
    const currentTrackData = filteredTracks.find(t => t.id === trackId) || {};
    const safeDislikes = Array.isArray(dislikesArray) ? dislikesArray : [];
    const isCurrentlyDisliked = safeDislikes.includes(voterId);
    try {
      const updatedDislikes = isCurrentlyDisliked
        ? safeDislikes.filter(id => id !== voterId)
        : [...safeDislikes, voterId];
      const newScore = calculateTrackScore({ ...currentTrackData, dislikes: updatedDislikes });
      await updateDoc(trackRef, {
        dislikes: isCurrentlyDisliked ? arrayRemove(voterId) : arrayUnion(voterId),
        likes: arrayRemove(voterId),
        onAir: newScore > 0,
        score: newScore
      });
      if (!isCurrentlyDisliked) showToast("Feedback gesendet!");
    } catch (err) { console.error(err); }
  };

  const toggleOnAir = async (e, track) => {
    e.stopPropagation();
    try {
      if (!track.onAir) {
        await updateDoc(doc(db, "tracks", track.id), { 
          onAir: true, score: 14, skipCount: 0,
          // ✅ NEU:
          onAirLog: arrayUnion({ 
            changedAt: new Date(), 
            changedTo: true 
          }),
        });
        showToast("LIVE ON AIR! 📡");
      } else {
        await updateDoc(doc(db, "tracks", track.id), { 
          onAir: false, score: -1,
          // ✅ NEU:
          onAirLog: arrayUnion({ 
            changedAt: new Date(), 
            changedTo: false 
          }),
        });
        showToast("Vom Sender genommen");
      }
    } catch (err) { console.error(err); }
  };


  // ─── NORMALISIEREN DER TRACKS ───

  const runGlobalVolumeSync = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "tracks"));
      console.log(`Prüfe ${querySnapshot.size} Tracks...`);
  
      for (const trackDoc of querySnapshot.docs) {
        const data = trackDoc.data();
        
        // ✅ ÜBERSPRINGEN, wenn der Wert schon existiert
        if (data.volume_adjustment) {
          console.log(`Überspringe (bereits geeicht): ${data.title}`);
          continue;
        }
  
        if (!data.url) continue;
  
        try {
          console.log(`Analysiere jetzt: ${data.title}...`);
          
          // Timeout-Schutz für den Fetch
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 Sek. Limit
  
          const response = await fetch(data.url, { signal: controller.signal });
          clearTimeout(timeoutId);
  
          const arrayBuffer = await response.arrayBuffer();
          const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
          const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
  
          const float32Data = audioBuffer.getChannelData(0);
          let sumSquares = 0;
          for (let i = 0; i < float32Data.length; i++) {
            sumSquares += float32Data[i] * float32Data[i];
          }
          const rms = Math.sqrt(sumSquares / float32Data.length);
  
          const targetRms = 0.15;
          let adjustment = targetRms / rms;
          adjustment = Math.max(0.5, Math.min(2.5, adjustment));
  
          await updateDoc(doc(db, "tracks", trackDoc.id), {
            volume_adjustment: Number(adjustment.toFixed(2))
          });
  
          console.log(`✅ ${data.title}: Gain auf ${adjustment.toFixed(2)} gesetzt.`);
          await new Promise(r => setTimeout(r, 300));
          
        } catch (err) {
          console.error(`❌ Fehler bei ${data.title} (evtl. übersprungen):`, err);
        }
      }
      alert("Volume Sync abgeschlossen oder verbleibende Tracks verarbeitet! 🎉");
    } catch (err) {
      console.error("Globaler Sync Fehler:", err);
    }
  };


  // ─── PLAYLIST INHALT (geteilt zwischen User und Easter Egg) ───
  const renderPlaylistContent = () => (
    <div style={{ padding: "10px" }}>
      {/* GENRE FILTER */}
      {isStationPath && (
        <div style={{ display: "flex", gap: "5px", overflowX: "auto", marginBottom: "12px", scrollbarWidth: "none" }}>
          {(isUserAdmin ? adminStyles : styles).map(s => (
            <button key={s} onClick={() => setActiveFilter(s)} style={{ 
              padding: "5px 12px", borderRadius: "10px", border: "none", fontSize: "0.6rem", 
              background: activeFilter === s ? (s === "🗑 DISLIKED" ? "#ff1744" : "#00acc1") : "#222", 
              color: activeFilter === s ? "#000" : "#888", 
              whiteSpace: "nowrap" 
            }}>{s}</button>
          ))}
        </div>
      )}

      {/* SUCHE & SORTIERUNG */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "15px", alignItems: "center" }}>
        <div style={{ position: "relative", flex: 1 }}>
          <input 
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%", padding: "8px 10px 8px 30px", background: "#000", 
              border: "1px solid #333", borderRadius: "8px", color: "#fff", 
              fontSize: "0.75rem", outline: "none"
            }}
          />
          <span style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", fontSize: "0.7rem", opacity: 0.5 }}>🔍</span>
        </div>
        <select 
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          style={{
            background: "#111", color: "#00acc1", border: "1px solid #333", 
            borderRadius: "8px", fontSize: "0.6rem", padding: "7px 5px", 
            fontWeight: "bold", outline: "none"
          }}
        >
          <option value="newest">NEW</option>
          <option value="abc">A-Z</option>
          <option value="likes">♥</option>
        </select>
      </div>

      {/* TRACK LISTE */}
      {finalDisplayedTracks.map((t, idx) => {
        const tLikes = Array.isArray(t.likes) ? t.likes : [];
        const tDislikes = Array.isArray(t.dislikes) ? t.dislikes : [];
        return (
          <div key={t.id}             
            ref={currentTrack.id === t.id ? activeTrackRef : null}
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 10px", background: currentTrack.id === t.id ? "rgba(0,172,193,0.1)" : "none", borderRadius: "10px", marginBottom: "4px" }}>
            <div onClick={() => playTrack(t.id, true)} style={{ textAlign: "left", cursor: "pointer", flex: 1, userSelect: "text"  }}>
              <div style={{ fontSize: "0.85rem", color: currentTrack.id === t.id ? "#00acc1" : "#fff", userSelect: "text" }}>{t.title}</div>
              <div style={{ fontSize: "0.65rem", color: "#666", userSelect: "text" }}>{t.artist}</div>
            </div>
            <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
              {isStationPath && (isUserAdmin || (isUserArtist && t.userId === user?.uid)) && (
                <button onClick={(e) => toggleOnAir(e, t)} style={{ background: "none", border: "none", fontSize: "1rem", cursor: "pointer", opacity: t.onAir ? 1 : 0.2 }}>📡</button>
              )}
              <span style={{ fontSize: "0.7rem", color: "#666" }}>
                ♥ {tLikes.length} | 👎 {tDislikes.length}
                {isUserAdmin && (
                  <span style={{ 
                    marginLeft: "8px", 
                    // Dynamische Farbwahl basierend auf dem Score
                    color: t.score === undefined || t.score <= 0 
                      ? "#ff1744" // Rot für 0 oder undefined
                      : t.score <= 3 
                        ? "#ff9100" // Orange für "kurz vor dem Aus" (Score 1-3)
                        : "#00acc1", // Cyan für gesunde Tracks
                    fontWeight: "900",
                    // Optional: Ein leichtes Pulsieren für gefährdete Tracks
                    animation: t.score <= 3 ? "pulse 2s infinite" : "none"
                  }}>
                    ⚡{t.score !== undefined ? t.score : "—"}
                    {/* Kleiner Warnhinweis bei niedrigem Score */}
                    {t.score <= 1 && t.score !== undefined && (
                      <span style={{ fontSize: "0.5rem", marginLeft: "2px" }}>⚠️</span>
                    )}
                  </span>
                )}
              </span>
              {isStationPath && (isUserAdmin || (isUserArtist && t.userId === user?.uid)) && (
                <button onClick={(e) => { e.stopPropagation(); setEditingTrack(t); }} style={{ background: "none", border: "none", cursor: "pointer" }}>⚙️</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  // Neue Funktion, z.B. direkt vor dem return():
  const handleDownloadCurrentTrack = async () => {
    if (!currentTrack?.url) return;
    try {
      const response = await fetch(currentTrack.url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${currentTrack.title} - ${currentTrack.artist}.mp3`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      showToast("Download fehlgeschlagen: " + err.message, "error");
    }
  };

  return (
    <div style={{ textAlign: "center", padding: "1.5rem", color: "white", maxWidth: "600px", margin: "0 auto", paddingBottom: "120px" }}>
      
      {/* ─── MIXCLOUD EMBED (wenn aktiv) ─── */}
      {activeMixcloudItem && (
        <div style={{ marginBottom: '20px', borderRadius: '16px', overflow: 'hidden', border: '1px solid #d4a84340' }}>
          <div style={{ fontSize: '0.6rem', color: '#d4a843', letterSpacing: '2px', fontWeight: '900', padding: '10px 14px 0', textAlign: 'left' }}>
            ☁ MIXCLOUD STREAM
          </div>
          <div style={{ fontSize: '0.8rem', color: '#fff', padding: '4px 14px 8px', textAlign: 'left' }}>
            {activeMixcloudItem.title}
          </div>
          <iframe
            key={activeMixcloudItem.mixcloudKey}
            width="100%" height="120" frameBorder="0"
            src={`https://www.mixcloud.com/widget/iframe/?hide_cover=1&mini=1&autoplay=1&feed=${encodeURIComponent(activeMixcloudItem.mixcloudKey)}`}
            allow="autoplay"
            style={{ display: 'block' }}
          />
          <button
            onClick={() => setActiveMixcloudItem(null)}
            style={{ width: '100%', padding: '8px', background: 'none', border: 'none', borderTop: '1px solid #1a1a1a', color: '#555', fontSize: '0.6rem', cursor: 'pointer' }}
          >
            ✕ CLOSE MIX — BACK TO TRACKS
          </button>
        </div>
      )}
      
      <VinylPlayer isPlaying={isPlaying} currentTrack={currentTrack} />

      {/* PLAYER CONTROLS */}
      <div style={{ marginBottom: "25px", width: "100%" }}>
        <input type="range" min="0" max={duration || 0} value={progress} onChange={e => setAudioTime(e.target.value)} style={{ width: "100%", accentColor: "#00acc1", marginBottom: "5px", cursor: "pointer" }} />
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "15px" }}>
          <span style={{ fontSize: "0.6rem", color: "#666", fontFamily: "monospace" }}>{formatTime(progress)}</span>
          <span style={{ fontSize: "0.6rem", color: "#666", fontFamily: "monospace" }}>{formatTime(duration)}</span>
        </div>

        {/* SHUFFLE */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "12px" }}>
          <button onClick={() => setIsShuffle(!isShuffle)} style={{ background: "none", border: "none", cursor: "pointer", transition: "0.3s ease", padding: "5px" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={isShuffle ? "#00acc1" : "#fff"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="16 3 21 3 21 8"></polyline>
              <line x1="4" y1="20" x2="21" y2="3"></line>
              <polyline points="21 16 21 21 16 21"></polyline>
              <line x1="15" y1="15" x2="21" y2="21"></line>
              <line x1="4" y1="4" x2="9" y2="9"></line>
            </svg>
          </button>
        </div>

        {/* HAUPT-CONTROLS */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: "80px" }}>
          
          {/* DISLIKE */}
          <button onClick={() => toggleDislike(currentTrack.id, currentTrack.dislikes)} style={{ background: "none", border: "none", cursor: "pointer", width: "50px", textAlign: "center" }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill={currentTrack.dislikes?.includes(user?.uid || getAnonymousId()) ? "#ff1744" : "none"} stroke={currentTrack.dislikes?.includes(user?.uid || getAnonymousId()) ? "#ff1744" : "#fff"} strokeWidth="2">
              <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
            </svg>
            <span style={{ fontSize: "0.65rem", color: "#666", display: "block", marginTop: "4px", fontWeight: "bold" }}>{currentTrack.dislikes?.length || 0}</span>
          </button>

          {/* NAV & PLAY */}
          <div style={{ display: "flex", gap: "25px", alignItems: "center" }}>
            <button onClick={playPreviousTrack} style={{ background: "none", border: "none", cursor: "pointer", display: "flex" }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="19 20 9 12 19 4 19 20"></polygon>
                <line x1="5" y1="19" x2="5" y2="5"></line>
              </svg>
            </button>
            
            <button onClick={togglePlay} style={{ width: "65px", height: "65px", borderRadius: "50%", background: "white", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {isPlaying ? (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="#000"><rect x="6" y="4" width="4" height="16"></rect><rect x="14" y="4" width="4" height="16"></rect></svg>
              ) : (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="#000" style={{ marginLeft: "4px" }}><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
              )}
            </button>
            
            <button onClick={() => playNextTrack(true)} style={{ background: "none", border: "none", cursor: "pointer", display: "flex" }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="5 4 15 12 5 20 5 4"></polygon>
                <line x1="19" y1="5" x2="19" y2="19"></line>
              </svg>
            </button>
          </div>

          {/* LIKE */}
          <button onClick={() => toggleLike(currentTrack.id, currentTrack.likes)} style={{ background: "none", border: "none", cursor: "pointer", width: "50px", textAlign: "center", position: "relative", pointerEvents: "auto", zIndex: 10 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill={currentTrack.likes?.includes(user?.uid || getAnonymousId()) ? "#00acc1" : "none"} stroke={currentTrack.likes?.includes(user?.uid || getAnonymousId()) ? "#00acc1" : "#fff"} strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l8.78-8.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
            <span style={{ fontSize: "0.65rem", color: "#666", display: "block", marginTop: "4px", fontWeight: "bold" }}>{currentTrack.likes?.length || 0}</span>
          </button>
        </div>
      </div>


      {/* ✅ NEUER FILTER-BUTTON (Nur sichtbar für eingeloggte User) */}
      {user && (
        <div style={{ display: "flex", justifyContent: "center", margin: "10px 0 25px 0" }}>
          <button 
            onClick={() => setShowAllTracks(!showAllTracks)}
            style={{
              background: "rgba(0, 172, 193, 0.1)",
              border: "1px solid #00acc1",
              color: "#00acc1",
              padding: "8px 18px",
              borderRadius: "20px",
              fontSize: "0.65rem",
              fontWeight: "900",
              letterSpacing: "1px",
              cursor: "pointer",
              transition: "0.3s"
            }}
          >
            {showAllTracks 
              ? `ALL TRACKS (${safeFilteredTracks.length})` 
              : `PUBLIC SELECTION (${publicCount})`}
          </button>
        </div>
      )}


      {/* SHARE BUTTONS */}
      <div style={{ display: "flex", justifyContent: "space-between", gap: "20px", marginBottom: "30px" }}>
        <button onClick={() => handleShare('app')} className="btn-station-cyan">SHARE STATION 🔗</button>
        <button onClick={() => handleShare('track')} className="btn-station-cyan">SHARE TRACK 🎵</button>
      </div>

      {/* ✅ NEU: DOWNLOAD (nur Admin) */}
      {isUserAdmin && (
        <button onClick={handleDownloadCurrentTrack} className="btn-station-cyan" style={{ width: "100%", marginBottom: "30px" }}>
          ⬇️ DOWNLOAD CURRENT TRACK
        </button>
      )}


      {/* ✅ NEU: DJ FX MIXER – nur für djMode User */}
      {user && djMode ? (
        <DJMixSection
          audioContextRef={audioContextRef}
          limiterNodeRef={limiterNodeRef}
          sourceNodeRef={sourceNodeRef}
          audioRef={audioRef}
          isPlaying={isPlaying}
        />
      ) : user ? (
        // ✅ Locked State für eingeloggte User ohne djMode
        <div style={{ 
          marginBottom: "20px", padding: "12px 15px",
          background: "rgba(255,255,255,0.02)",
          border: "1px solid #1a1a1a", borderRadius: "12px",
          display: "flex", justifyContent: "space-between", alignItems: "center"
        }}>
          <span style={{ fontSize: "0.6rem", color: "#333", fontWeight: "900", letterSpacing: "2px" }}>🎛 DJ FX MIXER</span>
          <span style={{ fontSize: "0.55rem", color: "#333" }}>🔒 LOCKED</span>
        </div>
      ) : null}

      {/* ─── MIXCLOUD QUEUE ─── */}
      {isUserAdmin && (
        <div style={{ marginBottom: '20px', background: 'rgba(212,168,67,0.04)', border: '1px solid #d4a84325', borderRadius: '16px', overflow: 'hidden' }}>
          <div style={{ padding: '12px 14px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.6rem', color: '#d4a843', letterSpacing: '2px', fontWeight: '900' }}>☁ MIXCLOUD QUEUE</span>
            <span style={{ fontSize: '0.55rem', color: '#444' }}>{mixcloudQueue.length} MIX{mixcloudQueue.length !== 1 ? 'ES' : ''}</span>
          </div>

          {/* Input */}
          <div style={{ display: 'flex', gap: '6px', padding: '0 10px 10px' }}>
            <input
              value={mixcloudInput}
              onChange={e => setMixcloudInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addMixcloudToQueue()}
              placeholder="Mixcloud URL einfügen…"
              style={{ flex: 1, background: '#0a0a0a', border: '1px solid #d4a84340', borderRadius: '8px', padding: '7px 10px', color: '#fff', fontSize: '0.7rem', outline: 'none', fontFamily: 'monospace' }}
            />
            <button
              onClick={addMixcloudToQueue}
              style={{ background: '#d4a843', color: '#000', border: 'none', borderRadius: '8px', padding: '0 12px', fontWeight: '900', fontSize: '0.7rem', cursor: 'pointer' }}
            >+</button>
          </div>

          {/* Queue list */}
          {mixcloudQueue.map((item, idx) => (
            <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', borderTop: '1px solid #111' }}>
              <span style={{ fontSize: '0.65rem', color: '#444', width: '16px', textAlign: 'center' }}>{idx + 1}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</div>
                <div style={{ fontSize: '0.6rem', color: '#555', fontFamily: 'monospace' }}>{item.artist}</div>
              </div>
              <button
                onClick={() => setActiveMixcloudItem(item)}
                style={{ background: '#d4a84320', border: '1px solid #d4a84350', color: '#d4a843', borderRadius: '6px', padding: '4px 10px', fontSize: '0.6rem', fontWeight: '900', cursor: 'pointer' }}
              >▶ PLAY</button>
              <button
                onClick={() => setMixcloudQueue(q => q.filter((_, i) => i !== idx))}
                style={{ background: 'none', border: '1px solid #222', color: '#444', borderRadius: '6px', padding: '4px 8px', fontSize: '0.7rem', cursor: 'pointer' }}
              >✕</button>
            </div>
          ))}

          {mixcloudQueue.length === 0 && (
            <div style={{ padding: '10px 14px 14px', fontSize: '0.6rem', color: '#333', textAlign: 'center' }}>
              Mixcloud URL einfügen um einen Mix zur Queue hinzuzufügen
            </div>
          )}
        </div>
      )}


      {/* UPLOAD BUTTON */}
      {canUpload && (
        <div style={{ marginBottom: "20px", width: "100%", boxSizing: "border-box" }}>
          {!selectedFile ? (  
            <label className="btn-upload-area" style={{ 
              cursor: "pointer", display: "flex", flexDirection: "column", 
              alignItems: "center", justifyContent: "center", gap: "8px", 
              width: "100%", maxWidth: "100%", padding: "20px",
              boxSizing: "border-box",
              background: "rgba(0, 172, 193, 0.05)", 
              border: "2px dashed #333", borderRadius: "15px", color: "#888" 
            }}>  
              <span style={{ fontSize: "0.9rem", fontWeight: "900" }}>ADD NEW TRACK MP3</span>
              <input type="file" hidden accept=".mp3,audio/mpeg" onChange={(e) => { 
                const file = e.target.files[0];
                if (file) { setSelectedFile(file); setMeta({...meta, title: file.name.split('.')[0]}); }
              }} />
            </label>
          ) : (
            <div style={{ textAlign: "left", background: "#111", padding: "15px", borderRadius: "15px" }}>
              <input className="admin-input" placeholder="Titel" value={meta.title} onChange={e => setMeta({...meta, title: e.target.value})} />
              <input className="admin-input" placeholder="Artist" value={meta.artist} onChange={e => setMeta({...meta, artist: e.target.value})} />
              
              {/* ✅ NEU: Jahr */}
              <input className="admin-input" placeholder="Jahr" type="number" value={meta.year || ""} onChange={e => setMeta({...meta, year: parseInt(e.target.value)})} />

              {/* ✅ NEU: Genre/Style */}
              <select 
                value={meta.style || "Dubcircus"}
                onChange={e => setMeta({...meta, style: e.target.value})}
                style={{ width: "100%", padding: "10px", background: "#000", border: "1px solid #333", borderRadius: "8px", color: "#fff", fontSize: "0.75rem", marginBottom: "10px" }}
              >
                {["Dubcircus", "Reggae", "Electronic", "Ambient", "Experimental", "streetwise Lab", "SoundCheck"].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>

              <button onClick={startUpload} disabled={uploading} className="btn-station-cyan" style={{ width: "100%" }}>
                {uploading ? `UPLOADING ${uploadProgress}%` : "CONFIRM"}
              </button>
              <button onClick={() => setSelectedFile(null)} style={{ width: "100%", background: "none", border: "none", color: "#888", fontSize: "0.6rem", marginTop: "5px" }}>CANCEL</button>
            </div>
          )}
        </div>
      )} 
      

      {/* PLAYLIST – eingeloggte User normal, Gäste Easter Egg */}
      {user ? (
        // ✅ Eingeloggte User: normaler Archive Button
        <div style={{ background: "rgba(255,255,255,0.03)", borderRadius: "20px", border: "1px solid #222" }}>
          <button onClick={() => setIsArchiveOpen(!isArchiveOpen)} style={{ width: "100%", padding: "15px", background: "none", border: "none", color: "#00acc1", fontWeight: "bold", display: "flex", justifyContent: "space-between" }}>
            <span>OPEN ARCHIVE ({finalDisplayedTracks.length})</span>
            <span>{isArchiveOpen ? "▲" : "▼"}</span>
          </button>
          {isArchiveOpen && (
            <button
              onClick={() => activeTrackRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })}
              style={{ width: "100%", padding: "6px 15px", background: "none", border: "none", borderTop: "1px solid #111", color: "#666", fontSize: "0.6rem", cursor: "pointer", textAlign: "left" }}
            >
              ▶ NOW PLAYING: {currentTrack?.title}
            </button>
          )}
          {isArchiveOpen && renderPlaylistContent()}
        </div>
      ) : (
        // ✅ Gäste: Easter Egg oder unsichtbarer Klick-Bereich
        <>
          {!showPlaylist ? (
            // Unsichtbarer Bereich – 5x klicken zum Aktivieren
            <div
              onClick={() => {
                const newCount = easterEggClicks + 1;
                setEasterEggClicks(newCount);
                setTimeout(() => setEasterEggClicks(0), 3000);
                if (newCount >= 5) {
                  setShowPlaylist(true);
                  setIsArchiveOpen(true);
                  setEasterEggClicks(0);
                }
              }}
              style={{ height: "60px", cursor: "default", userSelect: "none" }}
            />
          ) : (
            // Easter Egg aktiviert – Playlist zeigen
            <div style={{ background: "rgba(255,255,255,0.03)", borderRadius: "20px", border: "1px solid #222", userSelect: "none" }}>
              {/* ✅ UP NEXT Button – auch zum Verstecken via 5x klicken */}
              <button 
                onClick={() => {
                  const newCount = easterEggClicks + 1;
                  setEasterEggClicks(newCount);
                  setTimeout(() => setEasterEggClicks(0), 3000);
                  if (newCount >= 5) {
                    setShowPlaylist(false);
                    setIsArchiveOpen(false);
                    setEasterEggClicks(0);
                  } else {
                    setIsArchiveOpen(!isArchiveOpen);
                  }
                }}
                style={{ width: "100%", padding: "15px", background: "none", border: "none", color: "#00acc1", fontWeight: "bold", display: "flex", justifyContent: "space-between", userSelect: "none" }}
              >
                <span>UP NEXT ({finalDisplayedTracks.length})</span>
                <span>{isArchiveOpen ? "▲" : "▼"}</span>
              </button>
              {/* ✅ NEU: NOW PLAYING auch beim Easter Egg */}
              {isArchiveOpen && (
                <button
                  onClick={() => activeTrackRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })}
                  style={{ width: "100%", padding: "6px 15px", background: "none", border: "none", borderTop: "1px solid #111", color: "#666", fontSize: "0.6rem", cursor: "pointer", textAlign: "left", userSelect: "none" }}
                >
                  ▶ NOW PLAYING: {currentTrack?.title}
                </button>
              )}

              {isArchiveOpen && renderPlaylistContent()}
            </div>
          )}
        </>
      )}

      



      {/* JOIN BUTTON – nur für nicht eingeloggte Gäste */}
      {!user && (
        <div style={{ textAlign: "center", marginTop: "40px", padding: "20px 0" }}>
          <p style={{ fontSize: "0.6rem", color: "#444", letterSpacing: "1px", marginBottom: "12px" }}>
            MITMACHEN UND EIGENE TRACKS UPLOADEN
          </p>
          <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
            <button onClick={onLoginClick} className="btn-station-cyan">
              ARTIST LOGIN
            </button>
            <button onClick={onJoinClick} className="btn-station-grey">
              JOIN COLLECTIVE
            </button>
          </div>
        </div>
      )}

      {/* EDIT TRACK MODAL */}
      {editingTrack && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.9)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 4000 }}>
          <div style={{ background: "#1a1f2b", padding: "25px", borderRadius: "20px", width: "90%", maxWidth: "400px" }}>
            <h3 style={{color: "#00acc1", fontSize: "0.9rem", marginBottom: "15px"}}>EDIT TRACK</h3>

            {/* ✅ ANZEIGE DES PRODUCERS / UPLOADERS */}
            <div style={{ fontSize: "0.6rem", color: "#666", marginBottom: "15px", letterSpacing: "1px" }}>
              UPLOADED BY: <span style={{ color: "#fff" }}>
                {allUsers?.find(u => u.id === editingTrack.userId || u.uid === editingTrack.userId)?.name || "UNKNOWN PRODUCER"}
              </span>
            </div>

            <input className="admin-input" placeholder="Titel" value={editingTrack.title} onChange={e => setEditingTrack({...editingTrack, title: e.target.value})} />
            <input className="admin-input" placeholder="Artist" value={editingTrack.artist} onChange={e => setEditingTrack({...editingTrack, artist: e.target.value})} />
            {/* ✅ NEU: Jahr */}
            <input className="admin-input" placeholder="Jahr" type="number" value={editingTrack.year || ""} onChange={e => setEditingTrack({...editingTrack, year: parseInt(e.target.value)})} />            
            {/* ✅ NEU: Genre/Style */}
            <select 
              value={editingTrack.style || "Dubcircus"}
              onChange={e => setEditingTrack({...editingTrack, style: e.target.value})}
              style={{ width: "100%", padding: "10px", background: "#000", border: "1px solid #333", borderRadius: "8px", color: "#fff", fontSize: "0.75rem", marginBottom: "10px" }}
            >
              {["Dubcircus", "Reggae", "Electronic", "Ambient", "Experimental", "streetwise Lab", "SoundCheck"].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            <button className="btn-station-cyan" style={{ width: "100%" }} onClick={async () => { 
              await updateDoc(doc(db, "tracks", editingTrack.id), { 
                title: editingTrack.title, 
                artist: editingTrack.artist,
                year: editingTrack.year || null,
                style: editingTrack.style || "Dubcircus"
              }); 
              setEditingTrack(null); showToast("Gespeichert! ✅");
            }}>SAVE</button>
            <button onClick={() => setEditingTrack(null)} style={{ width: "100%", marginTop: "10px", background: "none", color: "#888", border: "none" }}>CANCEL</button>
            {/* ✅ Löschen nur für eigene Tracks oder Admin */}
            {(isUserAdmin || editingTrack.userId === user?.uid) && (
              <button 
                onClick={async () => {
                  if (!window.confirm(`"${editingTrack.title}" wirklich löschen?`)) return;
                  try {
                    if (editingTrack.url) {
                      try {
                        const fileRef = ref(storage, editingTrack.url);
                        await deleteObject(fileRef);
                      } catch (e) {
                        console.log("Storage delete failed:", e);
                      }
                    }
                    await deleteDoc(doc(db, "tracks", editingTrack.id));
                    setEditingTrack(null);
                    showToast("Track gelöscht! 🗑");
                  } catch (err) {
                    showToast("Löschen fehlgeschlagen: " + err.message, "error");
                  }
                }}
                style={{ width: "100%", marginTop: "10px", background: "none", border: "1px solid #ff1744", color: "#ff1744", borderRadius: "8px", padding: "10px", fontWeight: "900", fontSize: "0.7rem", cursor: "pointer" }}
              >
                🗑 TRACK LÖSCHEN
              </button>
            )}
          </div>
        </div>
      )}

      
      {/* SOUND SYNC BUTTON

      {isUserAdmin && (
        <div style={{ 
          marginTop: "40px", 
          padding: "20px", 
          borderTop: "1px solid #333", 
          textAlign: "center" 
        }}>
          <p style={{ fontSize: "0.7rem", color: "#666" }}>ADMIN TOOLS</p>
          <button 
            onClick={runGlobalVolumeSync}
            style={{ 
              padding: "10px 20px", 
              background: "#ff1744", 
              color: "white", 
              border: "none", 
              borderRadius: "8px", 
              fontWeight: "bold",
              cursor: "pointer"
            }}
          >
            ⚡ START GLOBAL VOLUME SYNC
          </button>
        </div>
      )}              
      */}



    </div>
  );
}